import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export interface HandoffRule {
  condition: string;
  target_funnel_id: string | null; // null = keep current
  target_stage_slug: string;
}

interface FunnelOption {
  id: string;
  name: string;
}

interface StageOption {
  slug: string;
  name: string;
}

const CUSTOM_RULE_VALUE = "__custom__";

const HANDOFF_RULE_SUGGESTIONS = [
  {
    label: "Pedido explícito por humano",
    value: "Transfira para humano somente quando o cliente pedir explicitamente para falar com uma pessoa, humano, atendente, consultor ou responsável. Não transfira apenas porque ele relatou uma dor, dúvida comercial ou necessidade do negócio.",
  },
  {
    label: "Reclamação grave ou urgência",
    value: "Transfira para humano somente quando o cliente fizer uma reclamação grave, citar cobrança indevida, erro operacional, cancelamento, ameaça jurídica ou problema urgente que exija intervenção humana.",
  },
  {
    label: "Informação sensível ou contratual",
    value: "Transfira para humano quando a dúvida envolver contrato, pagamento, dados sensíveis, negociação especial ou decisão comercial que não esteja nas informações cadastradas do agente.",
  },
  {
    label: "Agente não conseguiu resolver",
    value: "Transfira para humano somente depois de tentar responder uma vez e ainda faltar informação confiável para resolver a solicitação. Não transfira antes de tentar ajudar com o contexto disponível.",
  },
  {
    label: "Cliente já tem atendimento em andamento",
    value: "Transfira para humano quando o cliente disser que já está falando com alguém da equipe, que já tem um caso em andamento ou que precisa continuar um atendimento humano anterior.",
  },
];

interface HandoffRuleInputProps {
  rules: HandoffRule[];
  onChange: (rules: HandoffRule[]) => void;
  workspaceId: string;
  maxItems?: number;
}

export function HandoffRuleInput({ rules, onChange, workspaceId, maxItems = 10 }: HandoffRuleInputProps) {
  const [funnels, setFunnels] = useState<FunnelOption[]>([]);
  const [stagesByFunnel, setStagesByFunnel] = useState<Record<string, StageOption[]>>({});

  const fetchFunnelsAndStages = useCallback(async () => {
    const { data: funnelsData } = await supabase
      .from("sales_funnels")
      .select("id, name")
      .eq("workspace_id", workspaceId)
      .order("order_index");

    if (funnelsData) {
      setFunnels(funnelsData);

      const { data: stagesData } = await supabase
        .from("lead_stages")
        .select("slug, name, funnel_id")
        .eq("workspace_id", workspaceId)
        .order("order_index");

      if (stagesData) {
        const grouped: Record<string, StageOption[]> = {};
        for (const stage of stagesData) {
          const key = stage.funnel_id || "__default__";
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push({ slug: stage.slug, name: stage.name });
        }
        setStagesByFunnel(grouped);
      }
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchFunnelsAndStages();
  }, [fetchFunnelsAndStages]);

  const addRule = () => {
    if (rules.length < maxItems) {
      onChange([...rules, { condition: "", target_funnel_id: null, target_stage_slug: "atendimento_humano" }]);
    }
  };

  const updateRule = (index: number, updates: Partial<HandoffRule>) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], ...updates };
    // If funnel changed, reset stage to first available
    if (updates.target_funnel_id !== undefined) {
      const funnelKey = updates.target_funnel_id || "__default__";
      const availableStages = stagesByFunnel[funnelKey] || [];
      const currentStageExists = availableStages.some(s => s.slug === updated[index].target_stage_slug);
      if (!currentStageExists && availableStages.length > 0) {
        updated[index].target_stage_slug = availableStages[0].slug;
      }
    }
    onChange(updated);
  };

  const removeRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const getSelectedSuggestion = (condition: string) => {
    return HANDOFF_RULE_SUGGESTIONS.some((suggestion) => suggestion.value === condition)
      ? condition
      : CUSTOM_RULE_VALUE;
  };

  const handleSuggestionChange = (index: number, value: string) => {
    if (value === CUSTOM_RULE_VALUE) {
      if (getSelectedSuggestion(rules[index]?.condition || "") !== CUSTOM_RULE_VALUE) {
        updateRule(index, { condition: "" });
      }
      return;
    }
    updateRule(index, { condition: value });
  };

  const getStagesForFunnel = (funnelId: string | null): StageOption[] => {
    const key = funnelId || "__default__";
    return stagesByFunnel[key] || [];
  };

  return (
    <div className="space-y-3">
      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">Nenhuma regra configurada</p>
      ) : (
        rules.map((rule, index) => (
          <div key={index} className="relative border border-border rounded-lg p-3 space-y-3 agent-field-enter bg-muted/30">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Sugestão de regra</Label>
                  <Select
                    value={getSelectedSuggestion(rule.condition)}
                    onValueChange={(val) => handleSuggestionChange(index, val)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Escolha uma regra segura ou personalize" />
                    </SelectTrigger>
                    <SelectContent>
                      {HANDOFF_RULE_SUGGESTIONS.map((suggestion) => (
                        <SelectItem key={suggestion.label} value={suggestion.value}>
                          {suggestion.label}
                        </SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_RULE_VALUE}>Personalizada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Condição personalizada</Label>
                  <Input
                    value={rule.condition}
                    onChange={(e) => updateRule(index, { condition: e.target.value })}
                    placeholder="Ex: Transfira somente quando o cliente pedir explicitamente para falar com um humano."
                    className="mt-1"
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Evite regras amplas como “quando tiver dor”, “quando tiver dúvida” ou “quando precisar de ajuda”.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Funil destino</Label>
                    <Select
                      value={rule.target_funnel_id || "__keep__"}
                      onValueChange={(val) => updateRule(index, { target_funnel_id: val === "__keep__" ? null : val })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecionar funil" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__keep__">Manter funil atual</SelectItem>
                        {funnels.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Estágio destino</Label>
                    <Select
                      value={rule.target_stage_slug}
                      onValueChange={(val) => updateRule(index, { target_stage_slug: val })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecionar estágio" />
                      </SelectTrigger>
                      <SelectContent>
                        {rule.target_funnel_id ? (
                          getStagesForFunnel(rule.target_funnel_id).map((s) => (
                            <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                          ))
                        ) : (
                          // Show all unique stages when "keep current funnel"
                          [...new Map(
                            Object.values(stagesByFunnel).flat().map(s => [s.slug, s])
                          ).values()].map((s) => (
                            <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeRule(index)}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-md mt-4"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRule}
        disabled={rules.length >= maxItems}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" /> Adicionar Regra
      </Button>

      {rules.length >= maxItems && (
        <p className="text-xs text-muted-foreground text-center">
          Limite de {maxItems} regras atingido
        </p>
      )}
    </div>
  );
}
